import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, MessageCircleQuestion, Pause, Play, X } from 'lucide-react';
import { voiceTutorChat, classmateTurnCheck, VoiceTutorMessage } from '../services/geminiService';
import { Language } from '../types';

// A virtual companion who sits in on a recorded lesson. His voice is mixed into the recording
// (connectAudio) and his card is drawn on screen, so both end up in the video. Two roles:
// - classmate ("Tèo"): listens while you explain and asks the questions a learner would;
// - professor: answers when you ask him, and politely steps in when the real-time check finds a
//   serious mistake (the `correction` cue), so the audience hears the fix from him.

interface Props {
    /** The note being explained — follows the note open in the expanded view as you navigate. */
    nodeId: string;
    nodeTitle: string;
    nodeContent: string;
    language: Language;
    /** Recording paused by hand: stop listening and do not speak. (An Auto-Pace idle pause does not count.) */
    paused: boolean;
    /** Route an audio element into the recording mix (and the speakers). Returns false if there is no mix. */
    connectAudio?: (el: HTMLAudioElement) => boolean;
    /** Called just before he speaks, so an idle-paused recording can resume first. */
    onWillSpeak?: () => void;
    /** Called into a recording already under way: he skips the greeting and asks straight away. */
    joinMidway?: boolean;
    role?: CompanionRole;
    /** Professor only: a serious mistake found by the real-time check. A new id is a new cue. */
    correction?: CompanionCorrection | null;
    onClose: () => void;
}

export type CompanionRole = 'classmate' | 'professor';
export interface CompanionCorrection { id: string; issue: string; suggestion?: string }

// The professor steps in at most this often, and drops a cue he could not use in time.
const CORRECTION_COOLDOWN_MS = 2 * 60 * 1000;
const CORRECTION_MAX_AGE_MS = 90 * 1000;
// Quiet needed before he cuts in with a correction (he never talks over the presenter).
const CORRECTION_QUIET_MS = 1500;

type Phase = 'starting' | 'listening' | 'thinking' | 'speaking' | 'holding' | 'error';

// Turn-taking is decided by the AI, not a timer: after each short pause in the teacher's speech,
// the words since Tèo last spoke go to classmateTurnCheck, which answers SPEAK only when the teacher
// clearly hands over (invites questions, or has finished answering his question) and WAIT otherwise.
// Pausing to think or draw is therefore safe. The "Hỏi đi" button / ` key still forces a turn.
const PAUSE_BEFORE_CHECK_MS = 1500;
// Words still being recognised when a turn starts get a moment to settle.
const SETTLE_MS = 700;

export function RecordingClassmate({ nodeId, nodeTitle, nodeContent, language, paused, connectAudio, onWillSpeak, joinMidway = false, role = 'classmate', correction = null, onClose }: Props) {
    const isVi = language === 'vietnamese';
    const isProf = role === 'professor';
    const name = isProf ? (isVi ? 'Giáo sư' : 'Professor') : 'Tèo';
    const [phase, setPhase] = useState<Phase>('starting');
    const [line, setLine] = useState('');            // what Tèo is saying / said last
    const [heard, setHeard] = useState('');          // live transcript of the teacher
    const [hold, setHold] = useState(false);         // "be quiet for now": keep listening, never take a turn by himself
    const [error, setError] = useState('');
    // Folded down to just the face and name, so he takes little room on screen (remembered).
    const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem('companion_collapsed') === '1'; } catch { return false; } });
    useEffect(() => { try { localStorage.setItem('companion_collapsed', collapsed ? '1' : '0'); } catch { /* private mode */ } }, [collapsed]);

    const phaseRef = useRef<Phase>('starting');
    phaseRef.current = phase;
    const holdRef = useRef(false);
    holdRef.current = hold;
    const pausedRef = useRef(paused);
    pausedRef.current = paused;
    const historyRef = useRef<VoiceTutorMessage[]>([]);
    const transcriptRef = useRef('');   // finalised words since his last turn
    const interimRef = useRef('');      // words still being recognised
    const speechSeqRef = useRef(0);     // bumps on every recognition result: a pending check is stale once it moves
    const checkingRef = useRef(false);
    const lastLineRef = useRef('');
    const recognitionRef = useRef<any>(null);
    const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const mouthRef = useRef<SVGEllipseElement | null>(null);
    const ampRafRef = useRef<number | null>(null);
    const unmountedRef = useRef(false);
    // Latest note, read at each turn (the turn logic is memoised); and the note of his last turn,
    // to tell him when the teacher has moved on to another note.
    const noteRef = useRef({ nodeId, nodeTitle, nodeContent });
    noteRef.current = { nodeId, nodeTitle, nodeContent };
    const lastTurnNodeIdRef = useRef<string | null>(null);
    const lastSpeechAtRef = useRef(0);                       // last time the presenter's words moved
    const pendingCorrectionRef = useRef<(CompanionCorrection & { at: number }) | null>(null);
    const lastInterjectAtRef = useRef(0);

    // While he talks, the open note glows with his voice (ExpandedNoteModal listens; --vt-amp is
    // the same amplitude variable the Voice Tutor uses).
    const setSpeakingSignal = (on: boolean) => {
        window.dispatchEvent(new CustomEvent('classmate-speaking', { detail: on }));
        if (!on) document.documentElement.style.removeProperty('--vt-amp');
    };

    const clearSilence = () => { if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; } };

    const stopListening = useCallback(() => {
        clearSilence();
        const rec = recognitionRef.current;
        recognitionRef.current = null;
        if (rec) { rec.onend = null; rec.onresult = null; rec.onerror = null; try { rec.stop(); } catch { /* already stopped */ } }
    }, []);

    // Mouth follows the voice: read the amplitude of the playing audio.
    const animateMouth = (audio: HTMLAudioElement) => {
        let analyser: AnalyserNode | null = null;
        try {
            const ctx = new AudioContext();
            // A media element can feed only one source node, so this analyser listens through a
            // captured stream instead of tapping the element (which connectAudio may already own).
            const stream = (audio as any).captureStream?.() as MediaStream | undefined;
            if (stream) {
                analyser = ctx.createAnalyser();
                analyser.fftSize = 256;
                ctx.createMediaStreamSource(stream).connect(analyser);
            }
            audio.addEventListener('ended', () => ctx.close().catch(() => {}), { once: true });
        } catch { /* no analyser: the mouth uses the CSS fallback */ }
        const data = analyser ? new Uint8Array(analyser.fftSize) : null;
        const loop = () => {
            if (analyser && data && mouthRef.current) {
                analyser.getByteTimeDomainData(data);
                let s = 0;
                for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; s += v * v; }
                const amp = Math.min(1, Math.sqrt(s / data.length) * 6);
                mouthRef.current.setAttribute('ry', String(1.5 + amp * 7));
                document.documentElement.style.setProperty('--vt-amp', (amp * 0.6).toFixed(3));
            }
            ampRafRef.current = requestAnimationFrame(loop);
        };
        ampRafRef.current = requestAnimationFrame(loop);
    };
    const stopMouth = () => {
        if (ampRafRef.current) cancelAnimationFrame(ampRafRef.current);
        ampRafRef.current = null;
        mouthRef.current?.setAttribute('ry', '1.5');
    };

    const speak = useCallback(async (text: string, audioBase64: string | null, mimeType: string) => {
        setLine(text);
        setPhase('speaking');
        onWillSpeak?.();
        await new Promise(r => setTimeout(r, 300)); // let an idle-paused recording resume before the first word
        if (unmountedRef.current) return;
        if (!audioBase64) {
            // No synthesized audio: show the line only — browser speech would not reach the recording.
            await new Promise(r => setTimeout(r, Math.min(8000, 1200 + text.length * 55)));
            return;
        }
        await new Promise<void>(resolve => {
            const bytes = Uint8Array.from(atob(audioBase64), c => c.charCodeAt(0));
            const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
            const audio = new Audio(url);
            audioRef.current = audio;
            const done = () => { stopMouth(); setSpeakingSignal(false); URL.revokeObjectURL(url); audioRef.current = null; resolve(); };
            audio.onended = done;
            audio.onerror = done;
            connectAudio?.(audio);
            audio.play().then(() => { setSpeakingSignal(true); animateMouth(audio); }).catch(done);
        });
    }, [connectAudio, onWillSpeak]);

    // Hand the turn to Tèo: send what the teacher said, speak his reply, then listen again.
    const takeTurn = useCallback(async (cue?: CompanionCorrection) => {
        if (unmountedRef.current || pausedRef.current) return;
        // Everything heard since his last turn, including words not yet finalised.
        const said = (transcriptRef.current + ' ' + interimRef.current).replace(/\s+/g, ' ').trim();
        transcriptRef.current = '';
        interimRef.current = '';
        setHeard('');
        stopListening();
        setPhase('thinking');
        const history = historyRef.current;
        const note = noteRef.current;
        // Moved to another note since his last turn (a cross-link, a child or parent note): say so,
        // so he can react to the new part and link it to what came before.
        const switched = lastTurnNodeIdRef.current !== null && lastTurnNodeIdRef.current !== note.nodeId;
        lastTurnNodeIdRef.current = note.nodeId;
        const moved = switched
            ? (isVi ? `(Người giảng vừa chuyển sang phần mới: "${note.nodeTitle}".) ` : `(The teacher just moved on to a new part: "${note.nodeTitle}".) `)
            : '';
        const userMessage = historyRef.current.length === 0 && !said
            ? ''
            : moved + (said || (isProf
                ? (isVi ? '(Người giảng tạm dừng.)' : '(The presenter paused.)')
                : (isVi ? '(Người giảng tạm dừng — hãy hỏi câu tiếp theo.)' : '(The teacher paused — ask your next question.)')));
        if (said || moved) history.push({ role: 'user', text: (moved + said).trim() });
        // A correction goes to the professor as a cue, not as something the presenter said.
        const cueText = cue
            ? `\n\n[CORRECTION CUE] Issue: ${cue.issue}${cue.suggestion ? `\nCorrect version: ${cue.suggestion}` : ''}`
            : '';
        try {
            const { text, audioBase64, audioMimeType } = await voiceTutorChat(
                note.nodeTitle, note.nodeContent, history, (userMessage || (cue ? '(The presenter paused.)' : '')) + cueText, isVi ? 'vietnamese' : 'english',
                isProf
                    ? { role: 'professor', style: isVi ? 'professor' : 'polite', voiceId: isVi ? 'vi-professor' : 'en-professor', speed: 1.0 }
                    : { role: 'classmate', style: isVi ? 'maytao' : 'friendly', voiceId: isVi ? 'vi-male-b' : 'en-male-a', speed: 1.05 },
                undefined, undefined, undefined,
                { joinMidway: joinMidway && history.length === 0 },
            );
            if (unmountedRef.current) return;
            history.push({ role: 'ai', text });
            lastLineRef.current = text;
            await speak(text, audioBase64, audioMimeType);
            if (!unmountedRef.current && !pausedRef.current) startListening();
        } catch (e: any) {
            if (unmountedRef.current) return;
            setError(isVi ? `${name} mất kết nối với AI. Bấm "${isProf ? 'Mời thầy' : 'Hỏi đi'}" để thử lại.` : `Lost the AI connection. Press "${isProf ? 'Your turn' : 'Ask'}" to retry.`);
            setPhase('error');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVi, isProf, speak, stopListening]);

    // A pause: ask the AI whether the teacher has handed over. Ignored if they spoke again meanwhile.
    const checkTurn = useCallback(async () => {
        if (checkingRef.current || unmountedRef.current || pausedRef.current || holdRef.current) return;
        if (phaseRef.current !== 'listening') return;
        const said = (transcriptRef.current + ' ' + interimRef.current).replace(/\s+/g, ' ').trim();
        if (!said) return;
        const seq = speechSeqRef.current;
        checkingRef.current = true;
        let speakNow = false;
        try { speakNow = await classmateTurnCheck(said, lastLineRef.current, noteRef.current.nodeTitle, role); } catch { speakNow = false; }
        checkingRef.current = false;
        if (unmountedRef.current || pausedRef.current || holdRef.current) return;
        if (seq !== speechSeqRef.current || phaseRef.current !== 'listening') return; // they kept talking
        if (speakNow) void takeTurn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [takeTurn]);

    const startListening = useCallback(() => {
        // @ts-ignore
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setError(isVi ? `Cần Chrome hoặc Edge để ${name} nghe được.` : `${name} needs Chrome or Edge to listen.`); setPhase('error'); return; }
        stopListening();
        setError('');
        setPhase(holdRef.current ? 'holding' : 'listening');
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = isVi ? 'vi-VN' : 'en-US';
        rec.onresult = (e: any) => {
            let interim = '';
            for (let i = e.resultIndex; i < e.results.length; i++) {
                if (e.results[i].isFinal) transcriptRef.current += e.results[i][0].transcript + ' ';
                else interim += e.results[i][0].transcript;
            }
            interimRef.current = interim;
            speechSeqRef.current++;
            lastSpeechAtRef.current = Date.now();
            setHeard((transcriptRef.current + interim).trim().slice(-160));
            clearSilence();
            if (!holdRef.current) silenceTimerRef.current = setTimeout(() => { void checkTurn(); }, PAUSE_BEFORE_CHECK_MS);
        };
        // Chrome ends a "continuous" session on its own every so often: keep listening.
        rec.onend = () => {
            if (recognitionRef.current !== rec) return;
            const p = phaseRef.current;
            if ((p === 'listening' || p === 'holding') && !pausedRef.current) { try { rec.start(); } catch { /* restarting */ } }
        };
        rec.onerror = (e: any) => { if (e?.error === 'not-allowed') { setError(isVi ? 'Chưa cho phép micro.' : 'Microphone not allowed.'); setPhase('error'); } };
        recognitionRef.current = rec;
        try { rec.start(); } catch { /* already started */ }
    }, [isVi, stopListening, checkTurn]);

    // He opens the lesson by himself.
    useEffect(() => {
        unmountedRef.current = false;
        void takeTurn();
        return () => {
            unmountedRef.current = true;
            stopListening();
            stopMouth();
            setSpeakingSignal(false);
            if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Manual pause of the recording: go quiet; on resume, listen again.
    useEffect(() => {
        if (paused) {
            stopListening();
            if (audioRef.current) audioRef.current.pause();
        } else if (phaseRef.current === 'listening' || phaseRef.current === 'holding') {
            startListening();
        } else if (audioRef.current && phaseRef.current === 'speaking') {
            audioRef.current.play().catch(() => {});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paused]);

    // Hold: keep listening but never take a turn alone (while drawing a long derivation, say).
    useEffect(() => {
        if (phaseRef.current === 'listening' || phaseRef.current === 'holding') {
            clearSilence();
            setPhase(hold ? 'holding' : 'listening');
        }
    }, [hold]);

    // Professor: keep the newest serious mistake, and step in at the next quiet moment.
    // A cue from before he was called in is old news: only mistakes made while he is present count.
    const cueAtJoinRef = useRef(correction?.id);
    useEffect(() => {
        if (!isProf || !correction || correction.id === cueAtJoinRef.current) return;
        pendingCorrectionRef.current = { ...correction, at: Date.now() };
    }, [isProf, correction?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isProf) return;
        const t = setInterval(() => {
            const cue = pendingCorrectionRef.current;
            if (!cue) return;
            const now = Date.now();
            if (now - cue.at > CORRECTION_MAX_AGE_MS) { pendingCorrectionRef.current = null; return; }
            if (holdRef.current || pausedRef.current || unmountedRef.current || checkingRef.current) return;
            if (phaseRef.current !== 'listening') return;
            if (now - lastSpeechAtRef.current < CORRECTION_QUIET_MS) return;
            if (now - lastInterjectAtRef.current < CORRECTION_COOLDOWN_MS) return;
            pendingCorrectionRef.current = null;
            lastInterjectAtRef.current = now;
            clearSilence();
            void takeTurn(cue);
        }, 500);
        return () => clearInterval(t);
    }, [isProf, takeTurn]);

    const askNow = useCallback(() => {
        const p = phaseRef.current;
        if (p === 'error') { void takeTurn(); return; }
        if (p !== 'listening' && p !== 'holding') return;
        clearSilence();
        setPhase('thinking');
        setTimeout(() => { void takeTurn(); }, SETTLE_MS); // let the last words finish recognising
    }, [takeTurn]);

    // ` hands the turn over from the keyboard (ignored while typing in a field).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== '`' || e.ctrlKey || e.metaKey || e.altKey) return;
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            e.preventDefault();
            askNow();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [askNow]);

    const status = paused ? (isVi ? 'tạm dừng' : 'paused')
        : phase === 'starting' || phase === 'thinking' ? (isVi ? 'đang nghĩ…' : 'thinking…')
        : phase === 'speaking' ? (isProf ? (isVi ? 'đang nói' : 'speaking') : (isVi ? 'đang hỏi' : 'asking'))
        : phase === 'holding' ? (isVi ? 'đang nghe (im lặng)' : 'listening (quiet)')
        : phase === 'listening' ? (isVi ? 'đang nghe' : 'listening')
        : (isVi ? 'lỗi' : 'error');

    return (
        <div className={`fixed left-5 bottom-5 z-[400] ${collapsed ? 'w-auto' : 'w-[340px]'} max-w-[calc(100vw-40px)] group select-none`}>
            <div className={`flex ${collapsed ? 'items-center gap-2 pr-2 py-1.5 pl-1.5' : 'items-start gap-3 px-3.5 py-3'} rounded-2xl bg-slate-900/90 backdrop-blur border border-white/10 shadow-2xl text-white`}>
                <div className={`relative flex-shrink-0 rounded-full p-0.5 transition-all ${phase === 'speaking' ? 'bg-gradient-to-br from-amber-300 to-orange-500 shadow-[0_0_18px_rgba(251,146,60,0.6)]' : 'bg-white/15'}`}>
                    {isProf
                        ? <ProfessorAvatar mouthRef={mouthRef} thinking={phase === 'thinking' || phase === 'starting'} size={collapsed ? 36 : 56} />
                        : <TeoAvatar mouthRef={mouthRef} thinking={phase === 'thinking' || phase === 'starting'} size={collapsed ? 36 : 56} />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">{name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${error ? 'bg-red-500/20 text-red-300' : phase === 'listening' && !paused ? 'bg-emerald-500/20 text-emerald-300' : phase === 'speaking' ? 'bg-orange-500/20 text-orange-300' : 'bg-white/10 text-white/60'}`}>{status}</span>
                        <button onClick={() => setCollapsed(c => !c)} title={collapsed ? (isVi ? 'Mở rộng' : 'Expand') : (isVi ? 'Thu nhỏ' : 'Collapse')}
                            className="ml-auto p-0.5 rounded text-white/40 hover:text-white hover:bg-white/10">
                            {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                    </div>
                    {collapsed ? null : error ? (
                        <p className="text-[12px] text-red-300 mt-1 leading-snug">{error}</p>
                    ) : line ? (
                        <p className="text-[14px] leading-snug mt-1 text-white/95 max-h-[7.5rem] overflow-y-auto">{line}</p>
                    ) : (
                        <p className="text-[12px] text-white/50 mt-1">{isVi ? `${name} đang vào lớp…` : `${name} is joining…`}</p>
                    )}
                    {!collapsed && heard && (phase === 'listening' || phase === 'holding') && (
                        <p className="text-[10px] text-white/40 mt-1.5 truncate">🎙 {heard}</p>
                    )}
                </div>
            </div>
            {/* Controls: faint until hovered, so they barely show in the video. */}
            <div className="flex items-center gap-1.5 mt-1.5 opacity-30 group-hover:opacity-100 transition-opacity">
                <button onClick={askNow} title={isProf ? (isVi ? 'Mời thầy nói ngay (phím `)' : 'Let the professor speak now (` key)') : (isVi ? 'Mời Tèo hỏi ngay (phím `)' : 'Let Tèo ask now (` key)')}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-900/90 border border-white/10 text-[11px] text-white hover:bg-slate-800">
                    <MessageCircleQuestion size={12} /> {isProf ? (isVi ? 'Mời thầy' : 'Your turn') : (isVi ? 'Hỏi đi' : 'Ask')} <kbd className="text-[9px] text-white/50">`</kbd>
                </button>
                <button onClick={() => setHold(h => !h)} title={isVi ? `${name} vẫn nghe nhưng không tự lên tiếng` : `${name} keeps listening but will not speak by himself`}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] ${hold ? 'bg-amber-500/80 border-amber-400 text-white' : 'bg-slate-900/90 border-white/10 text-white hover:bg-slate-800'}`}>
                    {hold ? <Play size={12} /> : <Pause size={12} />} {hold ? (isVi ? (isProf ? 'Cho thầy nói' : 'Cho hỏi lại') : (isProf ? 'Let him speak' : 'Let him ask')) : (isVi ? 'Im đã' : 'Hold')}
                </button>
                <button onClick={onClose} title={isVi ? (isProf ? 'Mời thầy nghỉ' : 'Cho Tèo nghỉ') : `Dismiss ${name}`}
                    className="ml-auto p-1 rounded-lg bg-slate-900/90 border border-white/10 text-white/70 hover:text-white">
                    <X size={12} />
                </button>
            </div>
        </div>
    );
}

/** Tèo: a cheerful cartoon classmate, drawn inline so it is crisp in the video. */
function TeoAvatar({ mouthRef, thinking, size = 56 }: { mouthRef: React.RefObject<SVGEllipseElement>; thinking: boolean; size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 64 64" className="rounded-full bg-sky-200 block" aria-label="Tèo">
            {/* shirt */}
            <path d="M10 64c2-11 11-16 22-16s20 5 22 16z" fill="#2563eb" />
            <path d="M27 48l5 6 5-6" fill="none" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
            {/* face */}
            <ellipse cx="32" cy="31" rx="15" ry="16" fill="#f6c99b" />
            <ellipse cx="17" cy="32" rx="2.5" ry="3.5" fill="#efb889" />
            <ellipse cx="47" cy="32" rx="2.5" ry="3.5" fill="#efb889" />
            {/* spiky hair */}
            <path d="M16 26c0-10 7-15 16-15s16 5 16 15c-3-3-5-6-6-9-2 3-5 5-9 5 1-2 1-4 0-5-3 3-8 5-13 5z" fill="#1f2937" />
            {/* eyes: look up while thinking */}
            <circle cx="26" cy={thinking ? 28 : 31} r="2.2" fill="#1f2937" />
            <circle cx="38" cy={thinking ? 28 : 31} r="2.2" fill="#1f2937" />
            <circle cx="26.8" cy={thinking ? 27.2 : 30.2} r="0.7" fill="#fff" />
            <circle cx="38.8" cy={thinking ? 27.2 : 30.2} r="0.7" fill="#fff" />
            {/* brows */}
            <path d="M22.5 26.5q3.5-2 7 0M34.5 26.5q3.5-2 7 0" stroke="#1f2937" strokeWidth="1.4" fill="none" strokeLinecap="round" />
            {/* cheeks */}
            <circle cx="22" cy="37" r="2.5" fill="#f59e9e" opacity="0.55" />
            <circle cx="42" cy="37" r="2.5" fill="#f59e9e" opacity="0.55" />
            {/* mouth: opens with his voice */}
            <ellipse ref={mouthRef} cx="32" cy="40" rx="4" ry="1.5" fill="#7f1d1d" />
        </svg>
    );
}

/** The professor: grey hair, glasses, a tie — calm and kind, drawn inline so it is crisp in the video. */
function ProfessorAvatar({ mouthRef, thinking, size = 56 }: { mouthRef: React.RefObject<SVGEllipseElement>; thinking: boolean; size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 64 64" className="rounded-full bg-indigo-200 block" aria-label="Professor">
            {/* jacket, shirt, tie */}
            <path d="M8 64c2-11 12-16 24-16s22 5 24 16z" fill="#334155" />
            <path d="M25 48l7 9 7-9z" fill="#f8fafc" />
            <path d="M31 50h2l1.5 3-2.5 8-2.5-8z" fill="#b91c1c" />
            {/* face */}
            <ellipse cx="32" cy="31" rx="14.5" ry="16" fill="#eec39a" />
            <ellipse cx="17.5" cy="32" rx="2.5" ry="3.5" fill="#e3b088" />
            <ellipse cx="46.5" cy="32" rx="2.5" ry="3.5" fill="#e3b088" />
            {/* grey hair: receding top, fuller sides */}
            <path d="M17 30c-1-9 5-16 15-16s16 7 15 16c-1-4-3-7-5-8 0 0-4 2-10 2s-10-2-10-2c-2 1-4 4-5 8z" fill="#cbd5e1" />
            {/* brows */}
            <path d="M22.5 25.5q3.5-1.6 7 0M34.5 25.5q3.5-1.6 7 0" stroke="#94a3b8" strokeWidth="1.8" fill="none" strokeLinecap="round" />
            {/* glasses */}
            <circle cx="26" cy="31" r="4.6" fill="#ffffff22" stroke="#1e293b" strokeWidth="1.4" />
            <circle cx="38" cy="31" r="4.6" fill="#ffffff22" stroke="#1e293b" strokeWidth="1.4" />
            <path d="M30.6 31h2.8" stroke="#1e293b" strokeWidth="1.4" />
            {/* eyes: look up while thinking */}
            <circle cx="26" cy={thinking ? 29 : 31} r="1.7" fill="#1f2937" />
            <circle cx="38" cy={thinking ? 29 : 31} r="1.7" fill="#1f2937" />
            {/* moustache */}
            <path d="M26 38.2q3-2 6-0.4q3-1.6 6 0.4q-3 1.2-6 0.2q-3 1-6-0.2z" fill="#cbd5e1" />
            {/* mouth: opens with his voice */}
            <ellipse ref={mouthRef} cx="32" cy="41.5" rx="3.6" ry="1.5" fill="#7f1d1d" />
        </svg>
    );
}
